'use client';

import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import {
  Brain,
  CheckCircle,
  Code,
  Github,
  Globe,
  Linkedin,
  Search,
  UploadCloud
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompaniesHero } from '@/components/ui/companies-hero';
import { Section } from '@/components/ui/section';
import Navbar from '@/components/ui/navBar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from '@/components/ui/select';
import Link from 'next/link';
import { supabase } from '@/lib/supabase';
import { CandidateList } from '@/components/candidate/CandidateList';
import { USPSection } from '@/components/blocks/usp-section';
import { StepSection } from '@/components/blocks/step-section';
import { NumberTicker } from '@/components/ui/number-ticker';
import Footer from '@/components/ui/footer';

// Update interface to match actual database schema
interface Candidate {
  id: string;
  candidate_name: string;
  skills: string[];
  technical_skills?: {
    score: number;
    feedback: string;
    recommendations: string[];
  };
  work_experiences: Array<{
    title: string;
    company: string;
    startDate: string;
    endDate: string;
  }>;
  educations?: Array<{
    degree: string;
    school: string;
    startDate: string;
    endDate: string;
  }>;
}

export default function CompaniesPage() {
  const { setTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    // Force light theme for this page
    setTheme('light');

    const fetchCandidates = async () => {
      const { data: candidates, error } = await supabase
        .from('candidates')
        .select('id, candidate_name, skills, work_experiences')
        .not('candidate_name', 'eq', '')
        .limit(6);

      if (error) {
        console.error('Error fetching candidates:', error);
        return null;
      }
      setCandidates(candidates as Candidate[]);
    };
    fetchCandidates();
  }, [setTheme]);

  return (
    <>
      <Navbar />
      <div className="flex flex-col min-h-screen">
        {/* {showBanner && (
          <div className="sticky top-16 w-full backdrop-blur-sm bg-yellow-50/80 border-b border-primary/10 z-40">
            <div className="max-w-7xl mx-auto px-4 py-2">
              <div className="flex justify-center items-center gap-2">
                <span className="text-sm text-gray-600">
                  Are you a software engineer looking for remote work in Europe?
                </span>
                <Link
                  href="/candidates"
                  className="text-sm font-medium text-primary hover:text-primary/80 flex items-center gap-1"
                >
                  Apply here
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="inline-block"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
                <button
                  onClick={() => setShowBanner(false)}
                  className="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label="Dismiss banner"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="text-gray-500"
                  >
                    <path
                      d="M13 1L1 13M1 1L13 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )} */}

        <CompaniesHero
          title="Remote Tech Hiring That You Can Rely On"
          subtitle="Hire at lightning speed with trust and confidence."
        />

        <Section className="py-12 px-4 lg:px-8 relative">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold font-domaine-narrow text-center mb-8 lg:text-6xl">
              Our Featured Candidates
            </h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <CandidateList filteredCandidates={candidates} limit={6} />
          </div>
          {/* Overlay CTA */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/90 to-transparent pt-16 pb-8">
            <div className="max-w-6xl mx-auto text-center">
              <p className="text-lg text-gray-600 mb-4">
                Create an account to be matched with our talent pool of 2500+
                candidates.
              </p>
              <Link href="/signup?role=company">
                <Button size="xl">Get Started</Button>
              </Link>
            </div>
          </div>
        </Section>
        <Section className="py-40 px-4 lg:px-8 relative">
          <USPSection />
        </Section>

        <Section className="py-40 px-4 bg-gradient-to-b from-[#2665eb] to-[#225ad3] text-white">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-5xl font-semibold lg:text-6xl font-domaine-narrow text-center mb-8">
              Our Numbers
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="text-4xl font-bold mb-2 ">
                  <NumberTicker value={72} /> h
                </div>
                <div className="text-sm font-inter">
                  Average time to qualified candidate
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold  mb-2 ">
                  <NumberTicker value={94} />%
                </div>
                <div className="text-sm font-inter">Pass probation period</div>
              </div>
              <div>
                <div className="text-4xl font-bold  mb-2 ">
                  <NumberTicker value={60} />%
                </div>
                <div className="text-sm font-inter">
                  Lower cost than traditional recruitment
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section className="py-40 px-4 lg:px-8 max-w-6xl mx-auto">
          <div className="max-w-6xl">
            <h2 className="text-5xl font-bold font-domaine-narrow mb-4 lg:text-6xl">
              How We Deliver Results
            </h2>
            <p className="text-gray-600 mb-12 max-w-2xl  text-xl">
              Our AI-powered platform handles the entire technical screening
              process, from initial assessment to in-depth technical validation.
            </p>

            <StepSection
              title="AI screening process"
              description="Our AI-powered platform handles the entire technical screening
              process, from initial assessment to in-depth technical validation."
              image="/ai-screening.png"
              steps={[
                {
                  title: 'Extract Candidates resume',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <UploadCloud className="size-5" />
                },
                {
                  title: 'Extract Candidates LinkedIn profile',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Linkedin className="size-5" />
                },
                {
                  title: 'Extract Candidates GitHub profile',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Github className="size-5" />
                },
                {
                  title: 'Extract Candidates portfolio website',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Globe className="size-5" />
                }
              ]}
            />

            <StepSection
              title="AI Interview process"
              description="Our AI-powered platform handles the entire technical screening
              process, from initial assessment to in-depth technical validation."
              image="/coding-interview.png"
              steps={[
                {
                  title: 'Our AI interviewer',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Brain className="size-5" />,
                  link: '/interview',
                  linkText: 'Learn more'
                },
                {
                  title: 'Live coding exercise',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Code className="size-5" />,
                  link: '/coding',
                  linkText: 'Learn more'
                }
              ]}
            />

            <StepSection
              title="Matching candidates with jobs"
              description="Our AI-powered platform handles the entire technical screening
              process, from initial assessment to in-depth technical validation."
              image="/ai-screening.png"
              steps={[
                {
                  title: 'Natural language candidate search',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Search className="size-5" />
                },
                {
                  title: 'Matching algorithm',
                  description:
                    'Our AI-powered platform handles the entire technical screening process, from initial assessment to in-depth technical validation.',
                  icon: <Brain className="size-5" />
                }
              ]}
            />
          </div>
        </Section>
        <Footer />
      </div>
    </>
  );
}
